import { Size } from "@/api/AppApi/common-interfaces/api-enums";
import {
  IProduct,
  IProducts,
} from "@/api/AppApi/services/home/interfaces/IProduct";

export const getProductBoxDefaultImageDim = (
  size: Size,
): { width: number; height: number } => {
  switch (size) {
    case Size.small:
      return { width: 148, height: 143 };
    case Size.vertical_small:
      return { width: 148, height: 240 };
    case Size.large:
      return { width: 273, height: 200 };
    case Size.vertical_large:
    default:
      return { width: 273, height: 434 };
  }
};

export const getProductBoxDefaultDimAndSpacing = (size: Size) => {
  switch (size) {
    case Size.small:
      return { width: 164, height: 311, spacing: 16 };
    case Size.vertical_small:
      return { width: 164, height: 408, spacing: 16 };
    case Size.large:
      return { width: 300, height: 389, spacing: 32 };
    case Size.vertical_large:
      return { width: 300, height: 623, spacing: 32 };
    default:
      return { width: 164, height: 311, spacing: 16 };
  }
};

const mulberry32 = (seed: number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/** Fallback seed derived from the product ids — stable across server/client for a dataset. */
const seedFromProducts = (products: { id?: number }[]): number => {
  let h = 2166136261;
  for (const p of products) {
    h ^= Number(p?.id) || 0;
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

// Fisher-Yates shuffle driven by a seeded PRNG so the order is reproducible.
const getRandomProducts = <T extends { id?: number }>(
  array: T[],
  count: number,
  seed?: number,
): T[] => {
  const random = mulberry32(seed ?? seedFromProducts(array));
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, count);
};

/**
 * Process products data with random selection and latest viewed merging
 * @param products - The products configuration and data
 * @param latestViewed - Array of latest viewed products (optional)
 * @param maxLatestViewed - Maximum number of products to show when merging with latest viewed (default: 25)
 * @param seed - Per-request shuffle seed generated on the server and passed down as a prop,
 *               so SSR and hydration produce the identical order. Omit to fall back to a
 *               seed derived from the product ids.
 * @returns Processed products with random selection and/or latest viewed merged
 */
export const processProductsData = (
  products: IProducts,
  latestViewed?: IProduct[],
  maxLatestViewed: number = 25,
  seed?: number,
): IProducts => {
  const backendData = Array.isArray(products?.data) ? products.data : [];

  // Step 1: Apply random selection to backend data if needed
  let processedBackendData = backendData;
  if (products.configurations?.has_random_relation) {
    const limit = products.configurations?.limit || backendData.length;
    if (backendData.length > limit) {
      processedBackendData = getRandomProducts(backendData, limit, seed);
    }
  }

  // Step 2: Handle latest browsed filter (merge with processed backend data)
  if (products.configurations?.has_latest_browsed_filter && latestViewed) {
    const seen = new Set(latestViewed.map((p) => p.id));
    const merged = [
      ...latestViewed,
      ...processedBackendData.filter((product) => !seen.has(product.id)),
    ].slice(0, maxLatestViewed);

    return {
      ...products,
      data: merged,
    };
  }

  // Step 3: Return processed data (with or without random selection)
  return {
    ...products,
    data: processedBackendData,
  };
};
